/**
 * FileTypeIcon Component
 * Icon representation for different file types with color coding
 */

import { forwardRef } from 'react';
import { Avatar } from '@/components/ui/data-display';
import { getFileIcon, getFileMimeColor } from '../utils';
import type { Icon } from '@tabler/icons-react';

export interface FileTypeIconProps {
  /** MIME type of the file */
  mimeType: string;
  /** Size of the icon avatar */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Custom color override (CSS color value) */
  color?: string;
  /** Border radius */
  radius?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Additional className */
  className?: string;
}

/**
 * Display a color-coded icon for a file type based on its MIME type
 *
 * @example
 * ```tsx
 * <FileTypeIcon mimeType="image/png" size="md" />
 * <FileTypeIcon mimeType="application/pdf" size="lg" />
 * <FileTypeIcon mimeType="video/mp4" color="#FF0000" />
 * ```
 */
export const FileTypeIcon = forwardRef<HTMLDivElement, FileTypeIconProps>(
  (props, ref) => {
    const {
      mimeType,
      size = 'md',
      color,
      radius = 'sm',
      className,
    } = props;

    const FileIcon: Icon = getFileIcon(mimeType);
    const defaultColor = getFileMimeColor(mimeType);
    const backgroundColor = color || defaultColor;

    // Icon sizes based on avatar size
    const iconSizes = {
      xs: 14,
      sm: 16,
      md: 20,
      lg: 24,
      xl: 28,
    };

    return (
      <Avatar
        ref={ref}
        size={size}
        radius={radius}
        style={{ backgroundColor }}
        className={className}
        aria-label={`File type: ${mimeType}`}
      >
        <FileIcon size={iconSizes[size]} color="white" />
      </Avatar>
    );
  }
);

FileTypeIcon.displayName = 'FileTypeIcon';

export default FileTypeIcon;
